import PropTypes from 'prop-types';
import cx from 'classnames';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { useState, useEffect, useCallback } from 'react';
import { createSortable } from '../../../utils/sorting';
import { isNewSong } from '../../../helpers/songDates';

import Icon from '../../common/Icon';
import ModalButton from '../../common/ModalButton';
import ServiceLength, { SongLength } from '../../common/ServiceLength';
import useSongLengths from '../../../hooks/useSongLengths';
import { totalSeconds } from '../../../helpers/serviceLength';
import { ReactComponent as MusicIcon } from '../../../icons/album-list.svg';
import { ReactComponent as TrashIcon } from '../../../icons/trash.svg';

import './SuggestSongsModal.scss';

const SuggestSongsModal = ({ suggestedSongs, onClearSongs, onSongReorder }) => {
  const { t } = useTranslation();
  const [linkCopied, setLinkCopied] = useState(false);
  const [songsListRef, setSongsListRef] = useState(null);
  const songLengths = useSongLengths(suggestedSongs);

  useEffect(() => {
    if (songsListRef && onSongReorder) {
      createSortable(songsListRef, onSongReorder);
    }
  }, [songsListRef, onSongReorder]);

  const generateSuggestLink = useCallback(() => {
    const songIds = suggestedSongs.map((song) => song._id).join(',');

    return `${window.location.origin}/songs?suggestedSongs=${songIds}`;
  }, [suggestedSongs]);

  const handleCopyLink = useCallback(async () => {
    const link = generateSuggestLink();

    // Limit song titles to avoid excessively long share text
    const maxTitles = 3;
    const titlesArr = suggestedSongs.map((song) => song.title);
    let songTitles;

    if (titlesArr.length > maxTitles) {
      const displayedTitles = titlesArr.slice(0, maxTitles).join(', ');
      songTitles = t('dialogs.suggestSongs.andMore', { titles: displayedTitles, count: titlesArr.length - maxTitles });
    } else {
      songTitles = titlesArr.join(', ');
    }

    const shareData = {
      title: t('dialogs.suggestSongs.shareTitle'),
      text: t('dialogs.suggestSongs.shareText', { titles: songTitles }),
      url: link,
    };

    // Check if Web Share API is available (mobile devices)
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        // User cancelled the share or error occurred
        // Fall through to clipboard copy
        if (error.name === 'AbortError') {
          return; // User cancelled, don't show error
        }
      }
    }

    // Fallback to clipboard copy for desktop/unsupported browsers
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      toast.error(t('dialogs.suggestSongs.copyFailed'));
    }
  }, [generateSuggestLink, suggestedSongs, t]);

  return (
    <div className="SuggestSongsModal">
      <ul className="songs" ref={(ref) => setSongsListRef(ref)}>
        {suggestedSongs.map((song) => {
          const category = isNewSong(song.createdAt, song.isNewSong)
            ? 'new'
            : song.category?.name === 'Praise'
              ? 'praise'
              : 'worship';

          return (
            <li key={song._id} className="song">
              {/* The category's round badge doubles as the handle the list is dragged by */}
              <span className={cx('category', 'drag-handle', category)}>
                <Icon icon={<MusicIcon />} />
              </span>
              <div className="info">
                <span className="title">{song.title}</span>
                <span className="artist">{song.artist}</span>
                <SongLength seconds={songLengths.lengths.get(song._id)} className="length" />
              </div>
            </li>
          );
        })}
      </ul>

      <ServiceLength
        seconds={totalSeconds(suggestedSongs, songLengths.lengths)}
        fromCache={songLengths.fromCache}
        className="total-length"
      />

      <div className="actions">
        <ModalButton block onClick={handleCopyLink}>
          {linkCopied ? t('dialogs.suggestSongs.copied') : t('dialogs.suggestSongs.share')}
        </ModalButton>
        <button type="button" className="clear" onClick={onClearSongs} aria-label={t('dialogs.suggestSongs.clearAll')}>
          <Icon icon={<TrashIcon />} />
        </button>
      </div>
    </div>
  );
};

SuggestSongsModal.propTypes = {
  suggestedSongs: PropTypes.array.isRequired,
  onClearSongs: PropTypes.func.isRequired,
  onSongReorder: PropTypes.func,
};

export default SuggestSongsModal;
